import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { MonthlyPlan } from '../../types';
import { StorageService } from '../../lib/storage';
import { getLocalDateString, getLocalMonthString } from '../../lib/dateUtils';
import {
  Printer,
  Save,
  CheckCircle2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  RotateCcw,
} from 'lucide-react';

export const MonthlyPlanView: React.FC = () => {
  const { user, triggerConfetti } = useApp();

  const defaultMonth = getLocalMonthString();

  const [selectedMonth, setSelectedMonth] = useState<string>(defaultMonth);
  const [isSavedAlert, setIsSavedAlert] = useState(false);

  // Form State
  const [formData, setFormData] = useState<MonthlyPlan>({
    month: defaultMonth,
    userName: user?.name || '',
    salahInJamatCommitment: false,
    salahNaflCommitment: false,
  });

  // Load saved plan when month changes
  useEffect(() => {
    const loaded = StorageService.getMonthlyPlan(selectedMonth);
    setFormData({
      ...loaded,
      month: selectedMonth,
      userName: loaded.userName || user?.name || '',
    });
  }, [selectedMonth, user?.name]);

  const handleChange = (field: keyof MonthlyPlan, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = () => {
    StorageService.saveMonthlyPlan({
      ...formData,
      month: selectedMonth,
    });
    setIsSavedAlert(true);
    triggerConfetti();
    setTimeout(() => setIsSavedAlert(false), 3500);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset this monthly plan to blank?')) {
      const blank: MonthlyPlan = {
        month: selectedMonth,
        userName: user?.name || '',
        salahInJamatCommitment: false,
        salahNaflCommitment: false,
      };
      setFormData(blank);
      StorageService.saveMonthlyPlan(blank);
    }
  };

  // Month navigation helpers
  const handlePrevMonth = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const prevDate = new Date(year, month - 2, 1);
    const prevMonthStr = `${prevDate.getFullYear()}-${String(prevDate.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(prevMonthStr);
  };

  const handleNextMonth = () => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const nextDate = new Date(year, month, 1);
    const nextMonthStr = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(nextMonthStr);
  };

  return (
    <div className="space-y-6 animate-fade-in pb-16 print:p-0 print:m-0 print:space-y-0 print:pb-0">
      {/* Top Action Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 p-3.5 sm:p-4 bg-white dark:bg-[#111827] border border-slate-200/90 dark:border-slate-800 rounded-2xl sm:rounded-3xl shadow-xs print:hidden">
        <div className="flex items-center gap-2.5 sm:gap-3">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-2xl bg-brand-500/10 text-brand-600 dark:text-brand-400 flex items-center justify-center font-bold shrink-0">
            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
              <span>Monthly Plan</span>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-brand-50 dark:bg-brand-950/60 text-brand-600 dark:text-brand-400 border border-brand-200 dark:border-brand-800">
                {selectedMonth}
              </span>
            </h2>
          </div>
        </div>

        <div className="flex items-center flex-wrap gap-2 w-full sm:w-auto justify-between sm:justify-end">
          {/* Month Switcher */}
          <div className="flex items-center bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 rounded-xl sm:rounded-2xl p-0.5 sm:p-1 shadow-xs flex-1 sm:flex-initial justify-between">
            <button
              onClick={handlePrevMonth}
              className="p-1.5 rounded-lg sm:rounded-xl hover:bg-white dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 transition-colors"
              title="Previous Month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-1.5 sm:px-2 py-1 text-xs font-bold bg-transparent text-slate-800 dark:text-slate-200 focus:outline-hidden text-center min-w-0"
            />
            <button
              onClick={handleNextMonth}
              className="p-1.5 rounded-lg sm:rounded-xl hover:bg-white dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 transition-colors"
              title="Next Month"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Reset */}
          <button
            onClick={handleReset}
            className="p-2 sm:p-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700/80 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-slate-400 hover:text-rose-500 rounded-xl sm:rounded-2xl text-xs font-bold transition-all shrink-0"
            title="Reset to blank"
          >
            <RotateCcw className="w-4 h-4" />
          </button>

          {/* Print 1-Page */}
          <button
            onClick={handlePrint}
            className="flex-1 sm:flex-initial px-3 sm:px-4 py-2 sm:py-2.5 bg-sky-600 hover:bg-sky-500 text-white rounded-xl sm:rounded-2xl text-xs font-bold shadow-md shadow-sky-500/20 flex items-center justify-center gap-1.5 transition-all whitespace-nowrap"
            title="Print exactly 1 single A4 page"
          >
            <Printer className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
            <span>Print 1-Page</span>
          </button>

          {/* Save Button */}
          <button
            onClick={handleSave}
            className="flex-1 sm:flex-initial px-4 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-brand-600 to-teal-500 hover:from-brand-500 hover:to-teal-400 text-white rounded-xl sm:rounded-2xl text-xs font-bold shadow-md shadow-brand-500/25 flex items-center justify-center gap-1.5 transition-all transform active:scale-95 whitespace-nowrap"
          >
            <Save className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
            <span>Save Plan</span>
          </button>
        </div>
      </div>

      {/* Save Success Alert */}
      {isSavedAlert && (
        <div className="flex items-center gap-2 p-3.5 px-5 bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-300 dark:border-emerald-800 rounded-2xl text-emerald-800 dark:text-emerald-200 text-xs font-bold shadow-xs animate-in fade-in slide-in-from-top-2 print:hidden">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <span>Monthly Plan for {selectedMonth} saved successfully!</span>
        </div>
      )}

      {/* Main Printable Sheet Container - Precision 1-Page Fit */}
      <div className="print-page-fit bg-white dark:bg-[#111827] text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-800 rounded-2xl sm:rounded-3xl p-3.5 sm:p-6 md:p-8 shadow-sm print:shadow-none print:border-none print:p-0 print:m-0 print:rounded-none font-sans text-xs print:text-[8px] print:leading-tight">
        {/* Document Header */}
        <div className="text-center mb-3.5 sm:mb-4 print:mb-1 space-y-0.5 print:space-y-0">
          <div className="text-xs print:text-[8.5px] font-semibold tracking-wide text-slate-600 dark:text-slate-400 font-serif">
            بِسْمِ اللَّهِ الرَّحْمَٰনِ الرَّحِيمِ
          </div>
          <h1 className="text-base sm:text-xl print:text-xs font-black tracking-tight text-slate-900 dark:text-white uppercase">
            Monthly Plan
          </h1>
        </div>

        {/* User Info & Month Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 print:flex-row print:gap-2 pb-3 print:pb-1 border-b border-slate-200 dark:border-slate-800 mb-3.5 sm:mb-4 print:mb-1.5 text-xs print:text-[8px]">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <span className="font-bold text-slate-700 dark:text-slate-300 whitespace-nowrap text-xs">Name:</span>
            <input
              type="text"
              placeholder="Your Name"
              value={formData.userName || ''}
              onChange={(e) => handleChange('userName', e.target.value)}
              className="flex-1 sm:w-48 print:w-40 min-w-0 px-2 py-0.5 bg-slate-50 dark:bg-slate-900 border-b border-slate-300 dark:border-slate-700 font-semibold focus:outline-hidden"
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <span className="font-bold text-slate-700 dark:text-slate-300 whitespace-nowrap text-xs">Month:</span>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="flex-1 sm:w-auto min-w-0 px-2 py-0.5 bg-slate-50 dark:bg-slate-900 border-b border-slate-300 dark:border-slate-700 font-bold focus:outline-hidden"
            />
          </div>
        </div>

        {/* SECTION 1: 2-Column Grid for Primary Practices */}
        <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-3 print:gap-1">
          {/* 1. Quran Study Target */}
          <div className="border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
            <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px] pb-1 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
              Quran Study
            </h3>
            <div className="grid grid-cols-2 gap-2 print:gap-1 pt-2 print:pt-0.5">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 print:hidden" />
                <span className="text-slate-600 dark:text-slate-400 font-medium">Total Days:</span>
                <input
                  type="text"
                  placeholder="0"
                  value={formData.quranTotalDays || ''}
                  onChange={(e) => handleChange('quranTotalDays', e.target.value)}
                  className="w-14 print:w-10 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 print:hidden" />
                <span className="text-slate-600 dark:text-slate-400 font-medium">Avg. Verses:</span>
                <input
                  type="text"
                  placeholder="0"
                  value={formData.quranAvgVerses || ''}
                  onChange={(e) => handleChange('quranAvgVerses', e.target.value)}
                  className="w-14 print:w-10 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-1.5 col-span-2">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Surah Name:</span>
                <input
                  type="text"
                  placeholder="Surah name..."
                  value={formData.quranSurahName || ''}
                  onChange={(e) => handleChange('quranSurahName', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-1.5 col-span-2">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Dars Prep:</span>
                <input
                  type="text"
                  placeholder="Dars topic/surah..."
                  value={formData.quranDarsPrepared || ''}
                  onChange={(e) => handleChange('quranDarsPrepared', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-1.5 col-span-2">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Memorization (Ayat):</span>
                <input
                  type="text"
                  placeholder="e.g. 1-20"
                  value={formData.quranMemorizeVerses || ''}
                  onChange={(e) => handleChange('quranMemorizeVerses', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-1.5 col-span-2">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Meaning Study:</span>
                <input
                  type="text"
                  placeholder="Surah with translation..."
                  value={formData.quranSurahWithMeaning || ''}
                  onChange={(e) => handleChange('quranSurahWithMeaning', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>
            </div>
          </div>

          {/* 2. Hadith Study Target */}
          <div className="border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
            <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px] pb-1 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
              Hadith Study
            </h3>
            <div className="grid grid-cols-2 gap-2 print:gap-1 pt-2 print:pt-0.5">
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0 print:hidden" />
                <span className="text-slate-600 dark:text-slate-400 font-medium">Total Days:</span>
                <input
                  type="text"
                  placeholder="0"
                  value={formData.hadithTotalDays || ''}
                  onChange={(e) => handleChange('hadithTotalDays', e.target.value)}
                  className="w-14 print:w-10 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0 print:hidden" />
                <span className="text-slate-600 dark:text-slate-400 font-medium">Avg. Hadith:</span>
                <input
                  type="text"
                  placeholder="0"
                  value={formData.hadithAvgCount || ''}
                  onChange={(e) => handleChange('hadithAvgCount', e.target.value)}
                  className="w-14 print:w-10 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-1.5 col-span-2">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Book / Subject:</span>
                <input
                  type="text"
                  placeholder="e.g. Riyadus Saliheen..."
                  value={formData.hadithBookTopic || ''}
                  onChange={(e) => handleChange('hadithBookTopic', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-1.5 col-span-2">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Dars Prep:</span>
                <input
                  type="text"
                  placeholder="Dars topic/no..."
                  value={formData.hadithDarsPrepared || ''}
                  onChange={(e) => handleChange('hadithDarsPrepared', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-1.5 col-span-2">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Memorization:</span>
                <input
                  type="text"
                  placeholder="0"
                  value={formData.hadithMemorized || ''}
                  onChange={(e) => handleChange('hadithMemorized', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>

              <div className="flex items-center gap-1.5 col-span-2">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Selected Topic:</span>
                <input
                  type="text"
                  placeholder="Topic of study..."
                  value={formData.hadithTopic || ''}
                  onChange={(e) => handleChange('hadithTopic', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>
            </div>
          </div>

          {/* 3. Literature Study Target */}
          <div className="border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
            <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px] pb-1 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
              Literature Study
            </h3>
            <div className="space-y-2 print:space-y-0.5 pt-2 print:pt-0.5">
              <div className="grid grid-cols-3 gap-2 print:gap-1">
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Total:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.literatureTotalPages || ''}
                    onChange={(e) => handleChange('literatureTotalPages', e.target.value)}
                    className="w-12 print:w-8 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Islamic:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.literatureIslamic || ''}
                    onChange={(e) => handleChange('literatureIslamic', e.target.value)}
                    className="w-12 print:w-8 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Others:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.literatureOthers || ''}
                    onChange={(e) => handleChange('literatureOthers', e.target.value)}
                    className="w-12 print:w-8 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                  />
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Book Names:</span>
                <input
                  type="text"
                  placeholder="Books to read..."
                  value={formData.literatureBookNames || ''}
                  onChange={(e) => handleChange('literatureBookNames', e.target.value)}
                  className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                />
              </div>

              <div className="grid grid-cols-2 gap-2 print:gap-1">
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Note:</span>
                  <input
                    type="text"
                    placeholder="Notes count..."
                    value={formData.literatureBookNotes || ''}
                    onChange={(e) => handleChange('literatureBookNotes', e.target.value)}
                    className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Disc:</span>
                  <input
                    type="text"
                    placeholder="Discussion..."
                    value={formData.literatureDiscussionNotes || ''}
                    onChange={(e) => handleChange('literatureDiscussionNotes', e.target.value)}
                    className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 4. Academic Study & Salah Target */}
          <div className="border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
            <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px] pb-1 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
              Academic Study & Salah
            </h3>
            <div className="space-y-2 print:space-y-0.5 pt-2 print:pt-0.5">
              <div className="grid grid-cols-4 gap-1.5 print:gap-1">
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Days:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.academicTotalDays || ''}
                    onChange={(e) => handleChange('academicTotalDays', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Hours:</span>
                  <input
                    type="text"
                    placeholder="0.0"
                    value={formData.academicAvgHours || ''}
                    onChange={(e) => handleChange('academicAvgHours', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Class:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.academicTotalClasses || ''}
                    onChange={(e) => handleChange('academicTotalClasses', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Attnd:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.academicAttendance || ''}
                    onChange={(e) => handleChange('academicAttendance', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                  />
                </div>
              </div>

              {/* Salah Commitments */}
              <div className="pt-1.5 print:pt-0.5 border-t border-slate-200 dark:border-slate-800 space-y-1 print:space-y-0.5">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.salahInJamatCommitment || false}
                    onChange={(e) => handleChange('salahInJamatCommitment', e.target.checked)}
                    className="w-3 h-3 rounded border-slate-400 text-brand-600 accent-brand-600"
                  />
                  <span className="text-slate-700 dark:text-slate-300 text-[10.5px] print:text-[8px] font-medium">
                    Will perform all 5 prayers in Jamat Insha'Allah
                  </span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.salahNaflCommitment || false}
                    onChange={(e) => handleChange('salahNaflCommitment', e.target.checked)}
                    className="w-3 h-3 rounded border-slate-400 text-brand-600 accent-brand-600"
                  />
                  <span className="text-slate-700 dark:text-slate-300 text-[10.5px] print:text-[8px] font-medium">
                    Will perform regular Nafl & Tahajjud Insha'Allah
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* 5. Contacts & Outreach Target */}
          <div className="border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
            <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px] pb-1 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
              Contacts & Outreach Target
            </h3>
            <div className="grid grid-cols-3 print:grid-cols-4 gap-1.5 print:gap-0.5 pt-2 print:pt-0.5">
              {[
                { key: 'contactMember', label: 'Member' },
                { key: 'contactAssociate', label: 'Assoc' },
                { key: 'contactWorker', label: 'Worker' },
                { key: 'contactSupporter', label: 'Supprt' },
                { key: 'contactFriend', label: 'Friend' },
                { key: 'contactWellWisher', label: 'Wisher' },
                { key: 'contactSchoolStudent', label: 'Student' },
                { key: 'contactMeritoriousStudent', label: 'Talent' },
                { key: 'contactTeacher', label: 'Teacher' },
                { key: 'contactMuharrama', label: 'Muharr' },
                { key: 'contactVip', label: 'VIP' },
              ].map((item) => (
                <div key={item.key} className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium truncate" title={item.label}>
                    {item.label}:
                  </span>
                  <input
                    type="text"
                    placeholder="0"
                    value={(formData as any)[item.key] || ''}
                    onChange={(e) => handleChange(item.key as keyof MonthlyPlan, e.target.value)}
                    className="w-8 print:w-6 px-0.5 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* 6. Organizational Duties Target */}
          <div className="border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
            <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px] pb-1 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
              Organizational Duties
            </h3>
            <div className="space-y-2 print:space-y-0.5 pt-2 print:pt-0.5">
              <div className="grid grid-cols-3 gap-1.5 print:gap-1">
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Dawah D:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.dawahDays || ''}
                    onChange={(e) => handleChange('dawahDays', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Dawah H:</span>
                  <input
                    type="text"
                    placeholder="0.0"
                    value={formData.dawahAvgHours || ''}
                    onChange={(e) => handleChange('dawahAvgHours', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Persons:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.dawahPersons || ''}
                    onChange={(e) => handleChange('dawahPersons', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                  />
                </div>
              </div>

              <div className="pt-1.5 print:pt-0.5 border-t border-slate-200 dark:border-slate-800 grid grid-cols-2 gap-2 print:gap-1">
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Other D:</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={formData.orgOtherDays || ''}
                    onChange={(e) => handleChange('orgOtherDays', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-600 dark:text-slate-400 font-medium">Other H:</span>
                  <input
                    type="text"
                    placeholder="0.0"
                    value={formData.orgOtherAvgHours || ''}
                    onChange={(e) => handleChange('orgOtherAvgHours', e.target.value)}
                    className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 2: Material Distribution Targets */}
        <div className="mt-3 print:mt-1 border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
          <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px] pb-1 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
            Material Distribution Targets
          </h3>
          <div className="grid grid-cols-3 print:grid-cols-5 gap-x-4 print:gap-x-2 gap-y-1.5 print:gap-y-0.5 pt-2 print:pt-0.5">
            {[
              { key: 'distIslamicLiterature', label: 'Books' },
              { key: 'distChhatrasangbad', label: 'Magazine' },
              { key: 'distClassRoutine', label: 'Routine' },
              { key: 'distKishorPatrika', label: 'Youth Mag' },
              { key: 'distEnglishMagazine', label: 'Eng Mag' },
              { key: 'distStickerCard', label: 'Card' },
              { key: 'distYw', label: 'Y.W' },
              { key: 'distParichiti', label: 'Brochure' },
              { key: 'distGiftMessageEmail', label: 'Gift/Email' },
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between gap-1 border-b border-slate-200/80 dark:border-slate-800/80 pb-0.5">
                <span className="text-slate-700 dark:text-slate-300 font-medium truncate">
                  {item.label}:
                </span>
                <input
                  type="text"
                  placeholder="0"
                  value={(formData as any)[item.key] || ''}
                  onChange={(e) => handleChange(item.key as keyof MonthlyPlan, e.target.value)}
                  className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-right font-bold focus:outline-hidden"
                />
              </div>
            ))}
          </div>
        </div>

        {/* SECTION 3: Growth Targets */}
        <div className="mt-3 print:mt-1 border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
          <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px] pb-1 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
            Growth & Manpower Target
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-4 print:gap-2 pt-2 print:pt-0.5">
            <div className="space-y-1.5 print:space-y-0.5">
              {[
                { key: 'growthMember', label: 'Member' },
                { key: 'growthMemberCandidate', label: 'Member Cand.' },
                { key: 'growthAssociate', label: 'Associate' },
                { key: 'growthAssociateCandidate', label: 'Assoc Cand.' },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between gap-2 border-b border-slate-200/80 dark:border-slate-800/80 pb-0.5">
                  <span className="text-slate-700 dark:text-slate-300 font-medium whitespace-nowrap">
                    {item.label}
                  </span>
                  <input
                    type="text"
                    placeholder="0"
                    value={(formData as any)[item.key] || ''}
                    onChange={(e) => handleChange(item.key as keyof MonthlyPlan, e.target.value)}
                    className="w-14 print:w-10 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-right font-bold focus:outline-hidden"
                  />
                </div>
              ))}
            </div>

            <div className="space-y-1.5 print:space-y-0.5">
              {[
                { key: 'growthWorker', label: 'Worker' },
                { key: 'growthSupporter', label: 'Supporter' },
                { key: 'growthFriend', label: 'Friend' },
                { key: 'growthWellWisher', label: 'Well-wisher' },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between gap-2 border-b border-slate-200/80 dark:border-slate-800/80 pb-0.5">
                  <span className="text-slate-700 dark:text-slate-300 font-medium whitespace-nowrap">
                    {item.label}
                  </span>
                  <input
                    type="text"
                    placeholder="0"
                    value={(formData as any)[item.key] || ''}
                    onChange={(e) => handleChange(item.key as keyof MonthlyPlan, e.target.value)}
                    className="w-14 print:w-10 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-right font-bold focus:outline-hidden"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* SECTION 4: Baitulmal Contribution */}
        <div className="mt-3 print:mt-1 border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
          <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px] pb-1 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
            Baitulmal & Finance Contribution
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 print:grid-cols-6 gap-x-3 gap-y-2 print:gap-1 pt-2 print:pt-0.5">
            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Date:</span>
              <input
                type="date"
                value={formData.baitulmalPaymentDate || ''}
                onChange={(e) => handleChange('baitulmalPaymentDate', e.target.value)}
                className="flex-1 px-1 py-0.5 print:py-0 border-b border-slate-300 bg-transparent font-medium focus:outline-hidden"
              />
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Personal:</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={formData.baitulmalPersonalIncrease || ''}
                onChange={(e) => handleChange('baitulmalPersonalIncrease', e.target.value)}
                className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-bold text-right focus:outline-hidden"
              />
              <span className="text-slate-500 font-bold">৳</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Total Inc:</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={formData.baitulmalTotalIncrease || ''}
                onChange={(e) => handleChange('baitulmalTotalIncrease', e.target.value)}
                className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-bold text-right focus:outline-hidden"
              />
              <span className="text-slate-500 font-bold">৳</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Welfare:</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={formData.baitulmalStudentWelfare || ''}
                onChange={(e) => handleChange('baitulmalStudentWelfare', e.target.value)}
                className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-bold text-right focus:outline-hidden"
              />
              <span className="text-slate-500 font-bold">৳</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Table Bank:</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={formData.baitulmalTableBank || ''}
                onChange={(e) => handleChange('baitulmalTableBank', e.target.value)}
                className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-bold text-right focus:outline-hidden"
              />
              <span className="text-slate-500 font-bold">৳</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Charity:</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={formData.baitulmalCharityBox || ''}
                onChange={(e) => handleChange('baitulmalCharityBox', e.target.value)}
                className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-bold text-right focus:outline-hidden"
              />
              <span className="text-slate-500 font-bold">৳</span>
            </div>
          </div>
        </div>

        {/* SECTION 5: Miscellaneous & Self-Development */}
        <div className="mt-3 print:mt-1 border border-slate-300 dark:border-slate-700 rounded-xl p-3 print:p-1.5 bg-slate-50/50 dark:bg-slate-900/30 print:rounded-md">
          <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-wider text-[10.5px] print:text-[8.5px] pb-1 print:pb-0.5 border-b border-slate-200 dark:border-slate-800">
            Miscellaneous & Self Development
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 print:grid-cols-6 gap-x-3 gap-y-2 print:gap-1 pt-2 print:pt-0.5">
            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium">Self-Eval:</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={formData.miscSelfEvaluationDays || ''}
                onChange={(e) => handleChange('miscSelfEvaluationDays', e.target.value)}
                className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
              />
              <span className="text-slate-500 text-[9px] print:text-[7px]">Days</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium">Exercise:</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={formData.miscPhysicalExerciseDays || ''}
                onChange={(e) => handleChange('miscPhysicalExerciseDays', e.target.value)}
                className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
              />
              <span className="text-slate-500 text-[9px] print:text-[7px]">Days</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium">Paper:</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={formData.miscNewspaperDays || ''}
                onChange={(e) => handleChange('miscNewspaperDays', e.target.value)}
                className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
              />
              <span className="text-slate-500 text-[9px] print:text-[7px]">Days</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium">Rel. Cont:</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={formData.miscRelativeContact || ''}
                onChange={(e) => handleChange('miscRelativeContact', e.target.value)}
                className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
              />
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium">Grp Dawah:</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={formData.miscGroupDawahTimes || ''}
                onChange={(e) => handleChange('miscGroupDawahTimes', e.target.value)}
                className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
              />
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium">Msg/Email:</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={formData.miscDawahMessagesSent || ''}
                onChange={(e) => handleChange('miscDawahMessagesSent', e.target.value)}
                className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
              />
            </div>
          </div>

          {/* Skill Development Sub-row */}
          <div className="pt-2 print:pt-0.5 mt-2 print:mt-0.5 border-t border-slate-200 dark:border-slate-800 grid grid-cols-1 sm:grid-cols-3 print:grid-cols-3 gap-2 print:gap-1">
            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Computer:</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={formData.miscComputerLearningDays || ''}
                onChange={(e) => handleChange('miscComputerLearningDays', e.target.value)}
                className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
              />
              <span className="text-slate-500 text-[9px] print:text-[7px]">Days</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Language:</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={formData.miscLanguageLearningDays || ''}
                onChange={(e) => handleChange('miscLanguageLearningDays', e.target.value)}
                className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
              />
              <span className="text-slate-500 text-[9px] print:text-[7px]">Days</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Other:</span>
              <input
                type="text"
                placeholder="Skill name..."
                value={formData.miscSkillOther || ''}
                onChange={(e) => handleChange('miscSkillOther', e.target.value)}
                className="flex-1 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent font-medium focus:outline-hidden"
              />
            </div>
          </div>

          {/* Self Initiative Report Submission */}
          <div className="pt-2 print:pt-0.5 mt-2 print:mt-0.5 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 text-xs print:text-[8px] print:flex-row">
            <div className="flex items-center gap-2">
              <span className="text-slate-700 dark:text-slate-300 font-medium whitespace-nowrap">
                Report will be presented proactively:
              </span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={formData.miscReportShowTimes || ''}
                onChange={(e) => handleChange('miscReportShowTimes', e.target.value)}
                className="w-10 print:w-7 px-1 py-0.5 print:py-0 border-b border-dashed border-slate-400 bg-transparent text-center font-bold focus:outline-hidden"
              />
              <span className="text-slate-500 text-[9px] print:text-[7px]">Times</span>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-slate-600 dark:text-slate-400 font-medium whitespace-nowrap">Target Date:</span>
              <input
                type="date"
                value={formData.miscReportShowDate || ''}
                onChange={(e) => handleChange('miscReportShowDate', e.target.value)}
                className="px-1 py-0.5 print:py-0 border-b border-slate-300 bg-transparent font-medium focus:outline-hidden"
              />
            </div>
          </div>
        </div>

        {/* SECTION 6: Supervisor Guidance & Signature */}
        <div className="mt-3 print:mt-1 grid grid-cols-1 md:grid-cols-3 print:grid-cols-3 gap-4 print:gap-2 pt-1 print:pt-0">
          {/* Advice box */}
          <div className="md:col-span-2 print:col-span-2 space-y-1 print:space-y-0.5">
            <label className="font-bold text-slate-800 dark:text-slate-200 text-[10.5px] print:text-[8.5px] flex items-center gap-1">
              <span>Supervisor's Advice / Remarks:</span>
            </label>
            <textarea
              rows={2}
              placeholder="Supervisor or mentor's guidance for this month..."
              value={formData.supervisorAdvice || ''}
              onChange={(e) => handleChange('supervisorAdvice', e.target.value)}
              className="w-full p-2 print:p-1 bg-slate-50/70 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-700 rounded-xl text-xs print:text-[8px] print:h-8 focus:outline-hidden font-sans"
            />
          </div>

          {/* Signature and Save box */}
          <div className="flex flex-col justify-between p-3 print:p-1 bg-slate-50/60 dark:bg-slate-900/40 border border-slate-300 dark:border-slate-700 rounded-xl">
            <div className="space-y-1 print:space-y-0.5">
              <div className="text-[10px] print:text-[7.5px] font-bold text-slate-700 dark:text-slate-300">
                Signature & Date:
              </div>
              <div className="h-6 print:h-4 border-b border-dashed border-slate-400 flex items-end justify-center pb-0.5">
                <span className="text-[9px] print:text-[7px] italic text-slate-400">Sign / Verification</span>
              </div>
              <div className="text-[9px] print:text-[7px] text-right text-slate-400 font-mono">
                {formData.signatureDate || getLocalDateString()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
